export const FILM_DURATION = 24

export type SceneId = 'opener' | 'engineering' | 'kinetics' | 'finishes' | 'finale'

export interface FilmScene {
    id: SceneId
    number: string
    start: number
    end: number
    title: string
}

export const SCENES: FilmScene[] = [
    { id: 'opener', number: '01', start: 0, end: 4, title: 'Opener' },
    { id: 'engineering', number: '02', start: 4, end: 9, title: 'Engineering' },
    { id: 'kinetics', number: '03', start: 9, end: 14, title: 'Kinetics' },
    { id: 'finishes', number: '04', start: 14, end: 19, title: 'Finishes' },
    { id: 'finale', number: '05', start: 19, end: 24, title: 'Signature' }
]

export type FinishKey = 'signature' | 'champagne' | 'carbon' | 'mirror'

export const SHOWCASE_FINISHES: FinishKey[] = ['champagne', 'carbon', 'mirror']

// Key moments shared by the 3D engine and the typography overlay
export const MOMENT = {
    swoopEnd: 5.3,
    orbitStart: 9.35,
    orbitEnd: 12.3,
    capFocus: 12.9,
    triptychSwap: 14.15,
    triptychWide: 14.3,
    finishFocus: [15.3, 16.65, 17.95] as const,
    finishEnd: [16.3, 17.6, 19.0] as const,
    finaleSwap: 19.12,
    lock: 20.4
}

// Where the film jumps to when a visitor asks to watch one finish in motion
export const FINISH_SEEK: Record<'champagne' | 'carbon' | 'mirror', number> = {
    champagne: 15.0,
    carbon: 16.3,
    mirror: 17.6
}

export const clamp = (value: number, min = 0, max = 1): number => Math.min(max, Math.max(min, value))

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

export const progress = (t: number, start: number, end: number): number => clamp((t - start) / (end - start))

export const smoothstep = (edge0: number, edge1: number, x: number): number => {
    const t = clamp((x - edge0) / (edge1 - edge0))
    return t * t * (3 - 2 * t)
}

// 0..1 visibility envelope: fades in after start, fades out before end
export const envelope = (t: number, start: number, end: number, fadeIn = 0.3, fadeOut = 0.3): number => {
    return Math.min(progress(t, start, start + fadeIn), 1 - progress(t, end - fadeOut, end))
}

export const ease = {
    linear: (t: number): number => t,
    inCubic: (t: number): number => t * t * t,
    outCubic: (t: number): number => 1 - Math.pow(1 - t, 3),
    inOutCubic: (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
    outQuart: (t: number): number => 1 - Math.pow(1 - t, 4),
    inOutQuint: (t: number): number => (t < 0.5 ? 16 * Math.pow(t, 5) : 1 - Math.pow(-2 * t + 2, 5) / 2),
    outExpo: (t: number): number => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)),
    inOutSine: (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2,
    outBack: (t: number): number => {
        const c1 = 1.70158
        const c3 = c1 + 1
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
    }
}

export type EaseName = keyof typeof ease

export const sceneIndexAt = (t: number): number => {
    for (let i = 0; i < SCENES.length; i++) {
        if (t < SCENES[i].end) return i
    }
    return SCENES.length - 1
}

// Cinema timecode at 24 frames per second: MM:SS:FF
export const formatTimecode = (t: number): string => {
    const safe = Math.max(0, t)
    const minutes = Math.floor(safe / 60)
    const seconds = Math.floor(safe % 60)
    const frames = Math.floor((safe % 1) * 24)
    const pad = (n: number): string => String(n).padStart(2, '0')
    return `${pad(minutes)}:${pad(seconds)}:${pad(frames)}`
}

export const formatClock = (t: number): string => {
    const safe = Math.max(0, Math.round(t))
    return `00:${String(safe).padStart(2, '0')}`
}

// Main wheel spin speed (radians per second) across the film
export const wheelSpinSpeed = (t: number): number => {
    if (t < 4) return 0.12
    if (t < 9) return 0.3
    if (t < MOMENT.orbitEnd) return lerp(0.3, 1.8, smoothstep(9, 10.5, t))
    if (t < 14) return lerp(1.8, 2.6, smoothstep(MOMENT.orbitEnd, 13, t))
    if (t < 19) return 0.8
    if (t < MOMENT.lock) return 5.6 * (1 - smoothstep(19.95, MOMENT.lock, t))
    return 0.14 * smoothstep(MOMENT.lock + 0.4, MOMENT.lock + 2, t)
}

// Horizontal slide of the main wheel as it locks into center in the finale
export const finaleSlide = (t: number): number => {
    if (t < 19) return 0
    return 2.9 * (1 - ease.outQuart(progress(t, 19, MOMENT.lock)))
}
